/** @format */
import { TFunction } from 'i18next';

import { hasValue } from '@toucan/client/global/application';

import { removeMarkdownCharacters } from '@toucan/client/shared/helpers/markdown';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum MetaFactoryType {
    HOMEPAGE,
    SELL_FORM,
    CATEGORY,
    BROWSE_BRANDS,
    BRAND,
    MODEL,
    ORIGINAL_CONTENT,
    PRODUCT,
    AUTHOR,
    DEFAULT,
}

type MetaProvider = () => MetaMapping;

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type MetaMapping = {
    title: string;
    description: string;
};

export class MetaFactory {
    private readonly metaProvider: MetaProvider;

    public constructor(metaProvider: MetaProvider) {
        this.metaProvider = metaProvider;
    }

    public getMeta(): MetaMapping {
        return this.metaProvider();
    }
}

export const metaFactories = {
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    [MetaFactoryType.HOMEPAGE]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t('MPB | Buy or Sell Used Cameras & Lenses'),
                description: t(
                    'MPB is the largest global platform to buy, sell and trade used photo and video kit. The simple, safe and circular way to trade, upgrade and get paid.'
                ),
            };
        }),
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    [MetaFactoryType.SELL_FORM]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t(
                    'Sell or Trade Used Cameras, Lenses & Accessories | MPB'
                ),
                description: t(
                    'Trade in photo & video equipment hassle-free with our five star rated service! Get a commitment-free quote online today.'
                ),
            };
        }),
    [MetaFactoryType.CATEGORY]: (
        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
        t: TFunction,
        {
            metaTitle = '',
            metaDescription = '',
            categoryName = '',
            categoryDescription = '',
        }
    ): MetaFactory =>
        new MetaFactory(() => {
            // category has 2 fallbacks, first is the taxonomy driven categoryDescription
            // if that is undefined we have the generic copy
            const fallbackDescription = categoryDescription
                ? categoryDescription
                : t(
                      "Buy, sell and trade in used {{categoryName}}. All with a 12 month warranty. Great photo and video kit that doesn't cost the earth.",
                      {
                          categoryName,
                      }
                  );

            // first try and use the metaDescription, otherwise fallback
            const description = hasValue(metaDescription)
                ? metaDescription
                : fallbackDescription;

            return {
                title: `${
                    hasValue(metaTitle) ? metaTitle : categoryName
                } | MPB`,
                description,
            };
        }),
    [MetaFactoryType.BRAND]: (
        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
        t: TFunction,
        {
            metaTitle = '',
            metaDescription = '',
            brandName = '',
            brandDescription = '',
        }
    ): MetaFactory =>
        new MetaFactory(() => {
            // brand has 2 fallbacks, first is the taxonomy driven brandDescription
            // if that is undefined we have the generic copy
            const fallbackDescription = brandDescription
                ? brandDescription
                : t(
                      "Buy, sell and trade in used {{brandName}} equipment. All with a 12 month warranty. Great photo and video kit that doesn't cost the earth.",
                      {
                          brandName,
                      }
                  );

            // first try and use the metaDescription, otherwise fallback
            const description = hasValue(metaDescription)
                ? metaDescription
                : fallbackDescription;

            return {
                title: `${hasValue(metaTitle) ? metaTitle : brandName} | MPB`,
                description,
            };
        }),
    [MetaFactoryType.MODEL]: (
        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
        t: TFunction,
        // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
        modelName: string = '',
        isNew: boolean
    ): MetaFactory =>
        new MetaFactory(() => {
            if (isNew) {
                return {
                    title: t('{{modelName}} | MPB', {
                        modelName,
                    }),
                    description: t(
                        'Buy a {{modelName}}, trade-in your own, or sell for cash, with MPB.',
                        { modelName }
                    ),
                };
            }

            return {
                title: t('Used {{modelName}} | MPB', {
                    modelName,
                }),
                description: t(
                    'Buy a used {{modelName}}, trade-in your own, or sell for cash, with MPB.',
                    {
                        modelName,
                    }
                ),
            };
        }),
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    [MetaFactoryType.ORIGINAL_CONTENT]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t(
                    'MPB Original Content Hub for Blog Article Pages | MPB'
                ),
                description: t(
                    'Discover our latest photography & video articles via the MPB content hub: Competitions, Interviews, EDU, Kit Guides, Sustainability, Tips, Videos & Podcasts'
                ),
            };
        }),
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    [MetaFactoryType.BROWSE_BRANDS]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t('Shop by brand | MPB'),
                description: t(
                    'View our most popular brands or browse our stocked brands alphabetically'
                ),
            };
        }),
    [MetaFactoryType.PRODUCT]: (
        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
        t: TFunction,
        // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
        productName: string = '',
        isNew: boolean
    ): MetaFactory =>
        new MetaFactory(() => {
            if (isNew) {
                return {
                    title: t('{{productName}} | MPB', {
                        productName,
                    }),
                    description: t(
                        'Buy a {{productName}}, trade-in your own, or sell for cash, with MPB.',
                        { productName }
                    ),
                };
            }

            return {
                title: t('Used {{productName}} | MPB', {
                    productName,
                }),
                description: t(
                    'Buy a used {{productName}}, trade-in your own, or sell for cash, with MPB.',
                    {
                        productName,
                    }
                ),
            };
        }),
    [MetaFactoryType.AUTHOR]: (
        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
        t: TFunction,
        // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
        name: string = '',
        seoTitle?: string | null,
        seoDescription?: string | null
    ): MetaFactory =>
        new MetaFactory(() => {
            const seoTitleName = seoTitle ?? name;
            const title = t('{{title}} | MPB', {
                title: seoTitleName,
            });
            const description =
                seoDescription ??
                t(
                    "Browse {{name}}'s articles on MPB, the world's largest and most loved marketplace for buying and selling used cameras and photography gear.",
                    { name }
                );

            return {
                title,
                description,
            };
        }),
    [MetaFactoryType.DEFAULT]: (
        // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
        t: TFunction,
        pageName?: string,
        title?: string | null,
        description?: string | null
    ): MetaFactory =>
        new MetaFactory(() => {
            // the pageName can sometimes contain markdown, we don't want to render these in
            // the page title, so they should be removed
            const selectedTitle = title ?? removeMarkdownCharacters(pageName);
            return {
                title: selectedTitle
                    ? t('{{title}} | MPB', {
                          title: selectedTitle,
                          interpolation: { escapeValue: false },
                      })
                    : t('MPB'),
                description:
                    description ??
                    t(
                        'MPB is the largest global platform to buy, sell and trade used photo and video kit. The simple, safe and circular way to trade, upgrade and get paid.'
                    ),
            };
        }),
};
